import React, { useEffect, useState } from "react";
import classnames from "classnames";
import qs from "query-string";
import { Row, Col, Container } from "reactstrap";
import useApi from "../../hooks/useApi";
import plusIcon from "../../assets/plus-icon.svg";
import { states, Concerns, Outcomes } from "../GatewayForm/config/questionOptions";
import CardSection from "../Card/Card";
import Button from "../../components/Button/Button";
import DropdowFilter from "../../components/DropdownFilter/DropdownFilter";
import ResourceStack from "../../blocks/ResourceStack/ResourceStack";

import styles from "./ResourcesCards.module.scss";

const ResourcesCards = ({
  resultsView = false,
  collectionView = false,
  featuredPublication = null,
  page: pageProp = 1,
  limit = 16,
  filter: filterProps = {},
}) => {
  const [page, setPage] = useState(pageProp);
  const [publications, setPublications] = useState([]);
  const [filters, setFilters] = useState(null);
  const [activeFilter, setActiveFormatFilter] = useState("all");
  const [selectedFilters, setSelectedFilters] = useState({});
  const filter = { ...selectedFilters, limit, page, ...filterProps };
  const qsOptions = { arrayFormat: "bracket" };
  const filterQueryString = qs.stringify({ limit, page, ...filterProps }, qsOptions);

  // Get posts from API
  const [{ data, loading }, getPublications] = useApi(`get/publications?${filterQueryString}`);
  const isLastPage = page === data?.pagination?.total_pages * 1;
  const categories = [...(filters?.concerns || []), ...(filters?.outcomes || [])].filter((category) => category?.slug !== "sustainable-water-sharing");

  useEffect(() => {
    if (data && data.publications && data.publications.length && !publications.length) {
      setPublications(data.publications);
      if (!filters) setFilters(data.filters);
    }
  }, [data]);

  const loadMore = async (e) => {
    e.preventDefault();
    const filterQueryString = qs.stringify({ ...filter, page: page + 1 }, qsOptions);
    const { data } = await getPublications(`get/publications?${filterQueryString}`);
    setPage(page + 1);
    setPublications([...publications, ...(data.publications || [])]);
  };

  const queryDescription = [];
  const hasFilters = filter.state || filter.concern || filter.outcomes?.length;

  if (filter.state) {
    const state = states.find((state) => state.value === filter.state);
    if (state) {
      queryDescription.push(
        <span onClick={() => window.toggleGatewayForm("edit-guided-location")} style={{ cursor: "pointer" }}>
          {state?.label}
        </span>
      );
    }
  }

  if (filter.concern) {
    const concern = Concerns.find((concern) => concern.value === filter.concern);
    if (concern) {
      queryDescription.push(
        <span onClick={() => window.toggleGatewayForm("edit-guided-concerns")} style={{ cursor: "pointer" }}>
          {concern?.label}
        </span>
      );
    }
  }

  if (filter.outcomes && filter.outcomes.length) {
    const outcome = Outcomes.find((outcome) => filter.outcomes.includes(outcome.value));
    if (outcome) {
      queryDescription.push(
        <span onClick={() => window.toggleGatewayForm("edit-guided-outcomes")} style={{ cursor: "pointer" }}>
          {outcome.label}
          {filter.outcomes.length > 1 && <span className={styles.number}>+{filter.outcomes.length - 1}</span>}
        </span>
      );
    }
  }

  const toggleFilter = (name, value) => async (e) => {
    if (typeof e?.preventDefault === "function") e.preventDefault();
    const _selectedFilters = { ...selectedFilters }

    const isCategory = name === 'category';
    if (isCategory) {
      const isConcern = filters?.concerns.map(({ slug }) => slug).includes(value);
      const isOutcome = filters?.outcomes.map(({ slug }) => slug).includes(value);
      if (isConcern) {
        name = 'concern';
        _selectedFilters.outcome = undefined;
      } else if (isOutcome) {
        name = 'outcomes';
        _selectedFilters.concern = undefined;
      }
    }

    const filter = { ..._selectedFilters, limit, page, ...filterProps };
    const filterQueryString = qs.stringify({ ...filter, [name]: value, page: 1 }, qsOptions);
    const { data } = await getPublications(`get/publications?${filterQueryString}`);
    setSelectedFilters({ ..._selectedFilters, [name]: value });
    setPage(1);
    setPublications(data.publications || []);
    if (name === "format") setActiveFormatFilter(value);
  };

  // console.log({ data })

  const selectFilter = (e) => {
    const { name, value } = e.target;
    toggleFilter(name, value ? value : undefined)(e);
  };

  const first12Publications = publications.slice(0, 12);
  const after12Publications = [...publications];
  after12Publications.splice(0, 12);

  return (
    <>
      {resultsView ? (
        <>
          <Row className="px-2 px-md-0">
            <Col className={styles.containerResource}>
              {loading ? (
                <p style={{ marginTop: 30 }}>Loading resources for</p>
              ) : (
                <p style={{ marginTop: 30 }}>Showing {publications.length} resources for</p>
              )}
              <h3 className={styles.sectionLink} style={{ fontSize: 32 }}>
                {queryDescription.map((component, i) => (
                  <>
                    {component}
                    {i !== queryDescription.length - 1 ? ", " : null}
                  </>
                ))}
              </h3>
            </Col>
          </Row>
          {filters ? (
            <Row style={{ marginTop: 74 }} className={`${styles.overflow}`}>
              <Col md="12" className={styles.containerResource}>
                <ul className={styles.formatFilters}>
                  <li
                    className={classnames("ml-0", { "border-dark": activeFilter === "all" })}
                    onClick={toggleFilter("format", "all")}
                  >
                    <p className="small" style={{ marginBottom: 0 }}>
                      All
                    </p>
                  </li>
                  {filters?.formats
                    ? filters?.formats.map((format) => (
                      <li
                        className={classnames({ "border-dark": activeFilter === format.slug })}
                        onClick={toggleFilter("format", format.slug)}
                      >
                        <p className="small" style={{ marginBottom: 0 }}>
                          {format.name}
                        </p>
                      </li>
                    ))
                    : null}
                  {/* <li className={styles.list}>Factsheets</li>
                <li className={styles.list}>Guides</li>
                <li className={styles.list}>Submissions</li>
                <li className={styles.list}>Cases</li>
                <li className={`${styles.list} mr-4 mr-md-0`}>Blog</li> */}
                </ul>
              </Col>
            </Row>
          ) : null}
        </>
      ) : null}
      {collectionView ? (
        <Row className="mt-4 pt-4" id="browse-all">
          <Col
            style={{
              display: "flex",
              flexDirection: "row",
              flexWrap: "wrap",
              justifyContent: "space-between",
            }}
          >
            <h3 style={{ width: "max-content" }} className={styles.title}>
              Browse resources
            </h3>
            <ul className={styles.filters}>
              <li>Filter by</li>
              {filters?.locations ? (
                <li>
                  <DropdowFilter
                    name="state"
                    title="Location"
                    defaultOptions={filters?.locations}
                    onChange={selectFilter}
                    defaultValue={filter.state}
                  />
                </li>
              ) : null}
              {categories.length ? (
                <li>
                  <DropdowFilter
                    name="category"
                    title="Category"
                    defaultOptions={categories}
                    onChange={selectFilter}
                    defaultValue={filter?.outcomes || filter?.concern}
                  />
                </li>
              ) : null}
              {filters?.formats ? (
                <li>
                  <DropdowFilter
                    name="format"
                    title="Type"
                    defaultOptions={filters?.formats}
                    onChange={selectFilter}
                    defaultValue={filter.format}
                  />
                </li>
              ) : null}
            </ul>
          </Col>
        </Row>
      ) : null}
      {/* {collectionView ? (
        <Row className="mt-4 pt-4 mr-0 ml-0" style={{ width: "100%" }}>
          <Col className="pl-0 pr-0" xs="12" md="12" lg="7">
            <h2 className={styles.title}>Browse resources</h2>
          </Col>
          {filters ? <Col
            xs="12"
            md="6"
            lg="5"
            className={`${styles.overflow} d-flex pr-0 pl-0`}
          >
            <Row className="justify-content-between">
              <Col
                xs="2"
                lg="2"
                className={`mr-0 ${styles.listFirst} pr-0 pl-0`}
              >
                Filter by
              </Col>

              {filters?.locations ? (
                <Col
                  xs="3"
                  lg="3"
                  className={`mr-xs-0 pl-0 pr-0`}
                >
                  <DropdowFilter
                    name="locations"
                    title="Location"
                    options={filters?.locations}
                    selectedOption={selectFilter}
                  />
                </Col>
              ) : null}

              {filters?.categories ? (
                <Col xs="3" lg="3" className={`pl-0 pr-0`}>
                  <DropdowFilter
                    name="categories"
                    title="Category"
                    options={filters?.categories}
                    selectedOption={selectFilter}
                  />
                </Col>
              ) : null}
              {filters?.formats ? (
                <Col xs="3" lg="3" className={`pl-0 pr-0`}>
                  <DropdowFilter
                    name="formats"
                    title="Type"
                    options={filters?.formats}
                    selectedOption={selectFilter}
                  />
                </Col>
              ) : null}
            </Row>
          </Col> : null}
        </Row>
      ) : null} */}
      <Row className="mx-0 mt-3 pt-0">
        <Col className={styles.containerCardSmall}>
          <Row>
            {!loading && !publications.length ? (
              <Col md="12" className="my-4 p-0">
                <div>
                  <h2>Sorry!</h2>
                </div>
                <div style={{ maxWidth: 657 }}>
                  We don’t have any resources that match your search criteria at the moment.<br />
                  Please try again with different search terms, see if you are eligible for legal advice or try our{' '}
                  <a href="/other-useful-contacts">
                    other useful contacts
                  </a>.
                  {/* Please try again with different search terms,{" "}
                  <a href="/free-legal-advice">see if you are eligible for legal advice</a>
                  {" "}or try our{" "}
                  <a href="/other-useful-contacts">other useful contacts</a>. */}
                </div>
                <div className={classnames(styles.applyForLegalAdvice, "mt-4")}>
                  <div>
                    <a href="/legal-resources">
                      <Button text="Try searching again" variant="secondary" />
                    </a>
                  </div>
                  <div
                    className={"mt-3 mt-md-1 ml-3"}
                  >
                    <a href="/free-legal-advice">
                      Apply for legal advice
                    </a>
                  </div>
                </div>
              </Col>
            ) : (
              <>
                {hasFilters || !featuredPublication ? (
                  <Container>
                    <Row className="pl-1">
                      {publications.map((content) => (
                        <Col md="3" className={`pr-1 pl-0 pt-1`}>
                          <CardSection data={content} />
                        </Col>
                      ))}
                    </Row>
                  </Container>
                ) : (
                  <>
                    <ResourceStack
                      publications={first12Publications}
                      featuredPublication={featuredPublication}
                      cardLimit={12}
                      featuredPublicationPosition="right"
                      showTopCards
                      style={{ paddingTop: 0, margin: 0 }}
                    />
                    <Container>
                      <Row className="pl-1">
                        {page > 1
                          ? after12Publications.map((content) => (
                            <Col md="3" className={`pr-1 pl-0 pt-1`}>
                              <CardSection data={content} />
                            </Col>
                          ))
                          : null}
                      </Row>
                    </Container>
                  </>
                )}
              </>
            )}
          </Row>
        </Col>
      </Row>
      <Row className="py-4 my-4">
        {publications.length && !isLastPage ? (
          <Col className="text-center">
            <a href="/" onClick={loadMore} className={styles.sectionLink}>
              {loading ? (
                <span>Loading..</span>
              ) : (
                <>
                  Load {limit} more resources
                  <img
                    src={plusIcon}
                    style={{
                      height: 11.5,
                      marginLeft: "0.5rem",
                      marginBottom: "0.1rem",
                    }}
                  />
                </>
              )}
            </a>
          </Col>
        ) : null}
      </Row>
    </>
  );
};

export default ResourcesCards;
